import type { Locale } from '~/lib/i18n';
import type { InternalLink } from '~/lib/types/blocks';
import { experienceRoute, EXPERIENCES } from '~/lib/experiences/registry';
import { DEFAULT_LOCALE, SUPPORTED_LOCALES } from '~/lib/i18n';
import { internalLinkPath } from '~/lib/locale';

// Absolute URLs are required in sitemaps and hreflang links, and meta runs on the client too,
// so the production origin is pinned here rather than read from the request.
export const SITE_URL = 'https://jolandenef.dev';

export const CONTACT_EMAIL = 'contact@jolandenef.dev,';

export const CONTACT_PHONE_DISPLAY = '+32 471 32 54 79';

export const CONTACT_PHONE_TEL = '+32471325479';

// wa.me expects the number in international format without the leading '+' or any separators.
export const CONTACT_WHATSAPP_URL = 'https://wa.me/32471325479';

/** What Jolan does, in one line: the homepage h1 (the name itself is display type, not the heading). */
export const ROLE_HEADING: Record<Locale, string> = {
  en: 'Freelance web & three.js developer based in Belgium',
  nl: 'Freelance webdeveloper in Mechelen',
};

/**
 * The default locale (English) is served at the bare origin with no locale segment; every other
 * locale lives under its own path segment.
 *
 * @param locale - The locale whose homepage to link
 * @returns The absolute URL of that locale's homepage
 */
export function localeUrl(locale: Locale): string {
  return DEFAULT_LOCALE === locale ? SITE_URL : `${SITE_URL}/${locale}`;
}

/**
 * English is the default and fallback (x-default), served at the root; Dutch is the Belgian
 * regional alternate. Each copy names all of them so search engines serve each visitor their
 * own language instead of treating the copies as duplicates.
 *
 * @returns hreflang/href pairs: x-default + en at the root, nl-be at /nl
 */
export function localeAlternates(): Array<{ hrefLang: string; href: string }> {
  return [
    { hrefLang: 'x-default', href: localeUrl('en') },
    { hrefLang: 'en', href: localeUrl('en') },
    { hrefLang: 'nl-be', href: localeUrl('nl') },
  ];
}

/**
 * Builds the absolute URL for a given path within a locale, reusing the homepage URL logic so
 * the default locale stays at the bare origin and other locales keep their path segment. An
 * empty or root path yields the plain locale homepage.
 *
 * @param locale - The locale whose copy of the path to link
 * @param path - The path to append (leading slash is enforced); empty or '/' means the homepage
 * @returns The absolute URL of that locale's copy of the path
 */
export function localePathUrl(locale: Locale, path: string): string {
  if ('' === path || '/' === path) {
    return localeUrl(locale);
  }

  const normalized = path.startsWith('/') ? path : `/${path}`;
  return `${localeUrl(locale)}${normalized}`;
}

/**
 * The path-aware counterpart to {@link localeAlternates}: names each locale's copy of a single
 * path so search engines treat them as translations rather than duplicates.
 *
 * @param path - The path whose locale alternates to list
 * @returns hreflang/href pairs: x-default + en at the root copy, nl-be at the /nl copy
 */
export function pathAlternates(path: string): Array<{ hrefLang: string; href: string }> {
  return [
    { hrefLang: 'x-default', href: localePathUrl('en', path) },
    { hrefLang: 'en', href: localePathUrl('en', path) },
    { hrefLang: 'nl-be', href: localePathUrl('nl', path) },
  ];
}

// A 2x export of public/og-image.svg; social platforms don't accept SVG, so the PNG is what we link.
export const OG_IMAGE = {
  url: `${SITE_URL}/og-image.png`,
  width: 2400,
  height: 1260,
  alt: 'Jolan De Nef, Creative Developer',
} as const;

const OG_LOCALES: Record<Locale, string> = { en: 'en_US', nl: 'nl_BE' };

interface SocialMetaOptions {
  title?: string;
  description?: string;
  url: string;
  locale?: Locale;
}

/**
 * Open Graph and Twitter card tags for link previews. React Router only renders the deepest
 * route's `meta`, so every route spreads these in itself rather than inheriting them from root.
 *
 * @param options - Page title, description, absolute URL and (optionally) the active locale
 * @param options.title - Page title shown in the preview
 * @param options.description - Page description shown in the preview
 * @param options.url - Absolute URL of the page
 * @param options.locale - Active locale, emitted as og:locale
 * @returns Meta descriptors for og:* and twitter:* tags, all pointing at the shared OG image
 */
export function socialMeta({ title, description, url, locale }: SocialMetaOptions) {
  const optional = (property: string, content: string | undefined) => (undefined !== content && '' !== content ? [{ property, content }] : []);

  return [
    { property: 'og:type', content: 'website' },
    { property: 'og:site_name', content: 'Jolan De Nef' },
    { property: 'og:url', content: url },
    ...optional('og:title', title),
    ...optional('og:description', description),
    ...optional('og:locale', undefined !== locale ? OG_LOCALES[locale] : undefined),
    { property: 'og:image', content: OG_IMAGE.url },
    { property: 'og:image:width', content: String(OG_IMAGE.width) },
    { property: 'og:image:height', content: String(OG_IMAGE.height) },
    { property: 'og:image:alt', content: OG_IMAGE.alt },
    { name: 'twitter:card', content: 'summary_large_image' },
    { name: 'twitter:image', content: OG_IMAGE.url },
    { name: 'twitter:image:alt', content: OG_IMAGE.alt },
  ];
}

/**
 * @param pathname - The request path from a route's `meta` args
 * @returns The absolute production URL for that path
 */
export function absoluteUrl(pathname: string): string {
  return `${SITE_URL}${'/' === pathname ? '' : pathname}`;
}

export const SITEMAP_PATHS = [
  '/linktree',
  '/experiences',
  ...EXPERIENCES.map(({ slug }) => experienceRoute(slug)),
] as const;

// Pages with one URL for both languages (the locale is a `?lang` param), so no `/nl` copy to list.
export const SITEMAP_SINGLE_PATHS = ['/projects'] as const;

/**
 * Kept out of search on purpose: the CV is shared by link only, and the experiments are work in
 * progress. `:lang` matches any segment, so the experiments are disallowed under every prefix.
 */
export const DISALLOWED_PATHS = [
  '/cv',
  '/experiments',
  ...SUPPORTED_LOCALES.map(locale => `/${locale}/experiments`),
] as const;

interface SitemapEntry { loc: string; alternates: Array<{ hrefLang: string; href: string }> }

/**
 * @param page - A basic page reference with both locale slugs
 * @returns One entry per locale it has a slug in, each carrying the page's hreflang set (the Dutch
 *   and English slugs differ, so the alternates come from the slugs, not a shared path)
 */
function basicPageEntries(page: InternalLink): SitemapEntry[] {
  const urls = SUPPORTED_LOCALES.flatMap((locale) => {
    const path = internalLinkPath(page, locale);
    return undefined === path ? [] : [{ locale, href: `${SITE_URL}${path}` }];
  });
  const english = urls.find(({ locale }) => 'en' === locale);
  const alternates = [
    ...(undefined === english ? [] : [{ hrefLang: 'x-default', href: english.href }]),
    ...urls.map(({ locale, href }) => ({ hrefLang: 'nl' === locale ? 'nl-be' : locale, href })),
  ];

  return urls.map(({ href }) => ({ loc: href, alternates }));
}

/**
 * Lists every indexable page for the sitemap: the homepage and each path in {@link SITEMAP_PATHS}
 * once per supported locale with its full hreflang set, then the single-URL pages, then the CMS
 * basic pages.
 *
 * @param basicPages - Published basic pages, fetched by the sitemap loader
 * @returns One sitemap entry per indexable URL, each with its loc and locale alternates
 */
export function sitemapEntries(basicPages: InternalLink[] = []): SitemapEntry[] {
  const paths = ['', ...SITEMAP_PATHS];

  return [
    ...paths.flatMap(path =>
      SUPPORTED_LOCALES.map(locale => ({
        loc: localePathUrl(locale, path),
        alternates: pathAlternates(path),
      })),
    ),
    ...SITEMAP_SINGLE_PATHS.map(path => ({ loc: `${SITE_URL}${path}`, alternates: [] })),
    ...basicPages.flatMap(basicPageEntries),
  ];
}
